import React, { useEffect, useMemo, useRef, useState } from 'react'

import { AppId } from '@inno-tools/container-bridge'
import { IconSizes, MagnifyerLineIcon } from '@inno-ui/icons'
import cn from 'classnames'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { transformAppConfigToAppData } from 'shared/components/MainLayout/components/AppButton/utils/appDataTransform'
import { AppInstance } from 'shared/types'
import { AppConfig } from 'shared/types/app'
import { useCommonStore } from 'src/shared/stores/commonStore'

import { AppLocationType } from '../LaunchPad/LaunchPad.types'
import styles from './UniversalSearch.module.scss'

interface UniversalSearchProps {
  handleAppClick?: (
    app: AppInstance,
    type: AppLocationType | 'universalSearch'
  ) => void
}

export const UniversalSearch: React.FC<UniversalSearchProps> = ({
  handleAppClick
}) => {
  const { t } = useTranslation('translation')
  const { isSearchOpen, setIsSearchOpen } = useCommonStore()
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const { apps: availableApps } = useAvailableApps()

  const filteredApps = useMemo(() => {
    if (!query) return []
    const lowerQuery = query.toLowerCase()
    return Object.values(availableApps).filter(app => {
      const isHiddenProjectManagementV2 =
        app.id === AppId.PROJECT_MANAGEMENT_V2 &&
        process.env.REACT_APP_INCLUDE_PROJECT_MANAGEMENT_V2_IN_LAUNCHPAD ===
          'false'

      return (
        !isHiddenProjectManagementV2 &&
        (app.name.toLowerCase().includes(lowerQuery) ||
          app.label.toLowerCase().includes(lowerQuery))
      )
    })
  }, [query, availableApps])

  useEffect(() => {
    if (isSearchOpen) {
      setQuery('')
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isSearchOpen])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'k' && e.metaKey) {
      e.preventDefault()
      setIsSearchOpen(false)
      return
    }

    if (e.key === 'Escape') {
      setIsSearchOpen(false)
      return
    }

    if (filteredApps.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => (prev + 1) % filteredApps.length)
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(
        prev => (prev - 1 + filteredApps.length) % filteredApps.length
      )
      return
    }

    if (e.key === 'Enter' && filteredApps[selectedIndex]) {
      e.preventDefault()
      onAppClick(filteredApps[selectedIndex])
      return
    }
  }

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    setSelectedIndex(0)
  }

  const onAppClick = (app: AppConfig) => {
    const appData = transformAppConfigToAppData(app)
    handleAppClick?.(appData, 'universalSearch')
    setIsSearchOpen(false)
  }

  return (
    <AnimatePresence>
      {isSearchOpen && (
        <motion.div
          className={styles.searchOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsSearchOpen(false)}
        >
          <motion.section
            className={styles.searchContainer}
            initial={{ scale: 0.9, opacity: 0, y: -20, borderRadius: 24 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: -20 }}
            layout='size'
            onClick={e => e.stopPropagation()}
          >
            <motion.div
              className={styles.contentWrapper}
              layout='size'
              initial={{ borderRadius: 22 }}
            >
              <motion.header className={styles.searchHeader} layout='size'>
                <div className={styles.searchIcon}>
                  <MagnifyerLineIcon size={IconSizes.Size24} />
                </div>
                <input
                  ref={inputRef}
                  type='text'
                  className={styles.searchInput}
                  placeholder={
                    t('navbar.search_placeholder') ||
                    'Clusterix durchsuchen oder Frage stellen'
                  }
                  value={query}
                  onChange={handleQueryChange}
                  onKeyDown={handleKeyDown}
                />
              </motion.header>

              {filteredApps.length > 0 && (
                <ul className={styles.resultsList}>
                  {filteredApps.map((app, index) => (
                    <li
                      key={app.id}
                      className={cn(styles.resultItem, {
                        [styles.active]: index === selectedIndex
                      })}
                      onMouseEnter={() => setSelectedIndex(index)}
                      onClick={() => onAppClick(app)}
                    >
                      <img
                        src={app.appIcon || app.icon}
                        alt={app.name}
                        className={styles.appIcon}
                      />
                      <div className={styles.appInfo}>
                        <span className={styles.appName}>{app.name}</span>
                        <span className={styles.appLabel}>{app.label}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {query && filteredApps.length === 0 && (
                <div className={styles.noResults}>
                  {t('navbar.no_results_found') || 'Keine Ergebnisse gefunden'}
                </div>
              )}
            </motion.div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
